import React, { FC } from 'react';

import { Avatar, Box, SxProps, Typography } from '@mui/material';

import getLongTermContractLabels from '../LongTermContractLabels/LongTermContractLabels';
import { ReactComponent as FavIcon } from '../../../assets/svg/favIconWithBorder.svg';
import { SkeletonVariants } from '../../../helpers/Constants';
import { getDayMonthDateYear } from '../../Reports/helpers';
import WithSkeleton from '../WithSkeleton';

import styles from './styles';

type ILongTermAttributes = {
  contractStart?: string;
  contractEnd?: string;
  longTermShiftContractId?: string;
};

interface IPharmacyInfoCard {
  successAction: string;
  favorite?: boolean;
  logoImage: string;
  longTermAttributes?: ILongTermAttributes;
  isAutoSyncRecord?: boolean;
  customTitleStyle?: SxProps;
  title: string;
  city?: string;
  isBooked?: boolean;
  date?: string;
  customContent?:React.ReactNode;
}

const { text, circular, rounded } = SkeletonVariants;

const PharmacyInfoCard: FC<IPharmacyInfoCard> = ({
  successAction,
  favorite,
  logoImage,
  longTermAttributes,
  isAutoSyncRecord,
  customTitleStyle,
  title,
  city,
  isBooked,
  date,
  customContent,
}) => {
  const { longTermShiftContractId, contractEnd, contractStart } = longTermAttributes ?? {};
  const SkeletonAvatar = WithSkeleton({
    successAction,
    variant: circular,
  })(Avatar);

  const SkeletonChip = WithSkeleton({
    successAction,
    variant: rounded,
  })(Box);

  const SkeletonTypography = WithSkeleton({
    successAction,
    variant: text,
  })(Typography);

  const { RangeLabel } = getLongTermContractLabels({
    contractEndDate: contractEnd,
    contractStartDate: contractStart,
    successAction,
    isLongTerm: !!longTermShiftContractId,
  });

  return (
    <>
      <Box sx={styles.avatarContainer}>
        <SkeletonAvatar
          src={logoImage}
          sx={styles.image}
          imgProps={{ style: { objectFit: 'contain' } }}
        />
        <SkeletonChip sx={styles.favIconContainer}>
          {favorite && <FavIcon />}
        </SkeletonChip>
      </Box>
      <Box sx={[styles.infoContainer, isAutoSyncRecord ? styles.gutter : {}]}>
        <SkeletonTypography sx={[styles.title, customTitleStyle] as SxProps}>
          {title}
        </SkeletonTypography>

        {isBooked && (
          <SkeletonTypography sx={styles.addressText}>
            {city}
          </SkeletonTypography>
        )}

        {longTermShiftContractId ? (
          <Box sx={styles.cardContent}>
            <RangeLabel sx={styles.rangeLabel} />
          </Box>
        ) : (
          <SkeletonTypography sx={styles.date}>
            {`${getDayMonthDateYear(date ?? '')}`}
          </SkeletonTypography>
        )}
        {customContent}
      </Box>
    </>
  );
};

export default PharmacyInfoCard;
